import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import avo from 'analytics/avo';
import ProductCardContext from 'data-hooks/context/ProductCardContext';
import useProductLabelOverride from '@/hooks/useProductLabelOverride';
import mergeProductAndSelectedOptionData from './helpers/mergeProductAndSelectedOptionData';
import ProductCardStaticUI from './ProductCardStaticUI';
import getProductCardLayoutFlags from './helpers/getProductCardLayoutFlags';

// This provides a new simple interface to render a ProductCard. As we
// gradually move to this component, we should be able to identify which props
// in the main ProductCard component are unnecessary. After migration, we
// should be able to remove all unnecessary props and state from the main
// ProductCard, then we can merge this component into the ProductCard.
const ProductCardStatic = t0 => {
  const $ = _c(70);
  const {
    analyticsComponentName: t1,
    campaign,
    disableImageLazyLoading,
    disablePDPPrefetch: t2,
    prioritizeImage,
    enableMobileCarousel,
    filterStateKey: t3,
    hidden,
    id,
    initialOptionId: t4,
    isClearance,
    isFavorited,
    layout: t5,
    meta,
    moreVariantsCount,
    name,
    onClick,
    onProductClicked,
    onDisplayInShowroomSlug,
    path,
    position,
    productFamily,
    productLabel,
    productType,
    quickship,
    secondaryImage,
    secondaryImageCropData,
    slug,
    variantsToShow,
    variantsTotal,
    isHotspotCard,
    bypass_stock
  } = t0;
  const analyticsComponentName = t1 === undefined ? avo.Component.PRODUCT_CARD : t1;
  const disablePDPPrefetch = t2 === undefined ? false : t2;
  const filterStateKey = t3 === undefined ? "" : t3;
  const initialOptionId = t4 === undefined ? null : t4;
  const layout = t5 === undefined ? "full" : t5;
  const [selectedOptionId, setSelectedOptionId] = useState(initialOptionId);
  const labelOverride = useProductLabelOverride(id);
  let t6;
  if ($[0] !== initialOptionId) {
    t6 = () => {
      setSelectedOptionId(prevSelectedOptionId => {
        if (prevSelectedOptionId !== initialOptionId) {
          return initialOptionId;
        }
        return prevSelectedOptionId;
      });
    };
    $[0] = initialOptionId;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const resetToInitialOptionId = t6;
  let t7;
  if ($[2] !== resetToInitialOptionId) {
    t7 = () => {
      resetToInitialOptionId();
    };
    $[2] = resetToInitialOptionId;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== filterStateKey || $[5] !== name || $[6] !== resetToInitialOptionId) {
    t8 = [resetToInitialOptionId, filterStateKey, name];
    $[4] = filterStateKey;
    $[5] = name;
    $[6] = resetToInitialOptionId;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  useEffect(t7, t8);
  let selectedOption;
  let showAttributes;
  let showProductLabel;
  let showShowroomInfo;
  let showStockInfo;
  let showSwatches;
  let t9;
  if ($[8] !== bypass_stock || $[9] !== campaign || $[10] !== enableMobileCarousel || $[11] !== id || $[12] !== isClearance || $[13] !== isFavorited || $[14] !== labelOverride?.label || $[15] !== layout || $[16] !== meta || $[17] !== moreVariantsCount || $[18] !== name || $[19] !== onDisplayInShowroomSlug || $[20] !== path || $[21] !== position || $[22] !== productFamily || $[23] !== productLabel || $[24] !== productType || $[25] !== quickship || $[26] !== secondaryImage || $[27] !== secondaryImageCropData || $[28] !== selectedOptionId || $[29] !== slug || $[30] !== variantsToShow || $[31] !== variantsTotal) {
    selectedOption = variantsToShow?.find(option => !!selectedOptionId && option.id === selectedOptionId) ?? variantsToShow?.[0];
    let t10;
    if ($[39] !== layout) {
      t10 = getProductCardLayoutFlags(layout);
      $[39] = layout;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    ({
      showAttributes,
      showProductLabel,
      showShowroomInfo,
      showStockInfo,
      showSwatches
    } = t10);
    t9 = mergeProductAndSelectedOptionData({
      campaign,
      enableMobileCarousel,
      id,
      isClearance,
      isFavorited,
      moreVariantsCount,
      name,
      onDisplayInShowroomSlug,
      path,
      position,
      productFamily,
      productLabel: labelOverride?.label ?? productLabel,
      productType,
      quickship,
      secondaryImage,
      secondaryImageCropData,
      selectedOption,
      slug,
      variantsToShow,
      variantsTotal,
      meta,
      bypass_stock
    });
    $[8] = bypass_stock;
    $[9] = campaign;
    $[10] = enableMobileCarousel;
    $[11] = id;
    $[12] = isClearance;
    $[13] = isFavorited;
    $[14] = labelOverride?.label;
    $[15] = layout;
    $[16] = meta;
    $[17] = moreVariantsCount;
    $[18] = name;
    $[19] = onDisplayInShowroomSlug;
    $[20] = path;
    $[21] = position;
    $[22] = productFamily;
    $[23] = productLabel;
    $[24] = productType;
    $[25] = quickship;
    $[26] = secondaryImage;
    $[27] = secondaryImageCropData;
    $[28] = selectedOptionId;
    $[29] = slug;
    $[30] = variantsToShow;
    $[31] = variantsTotal;
    $[32] = selectedOption;
    $[33] = showAttributes;
    $[34] = showProductLabel;
    $[35] = showShowroomInfo;
    $[36] = showStockInfo;
    $[37] = showSwatches;
    $[38] = t9;
  } else {
    selectedOption = $[32];
    showAttributes = $[33];
    showProductLabel = $[34];
    showShowroomInfo = $[35];
    showStockInfo = $[36];
    showSwatches = $[37];
    t9 = $[38];
  }
  const mergedData = t9;
  const t10 = labelOverride?.color;
  const t11 = selectedOption?.id;
  let t12;
  if ($[41] !== campaign || $[42] !== isClearance || $[43] !== isFavorited || $[44] !== mergedData || $[45] !== position || $[46] !== productFamily || $[47] !== productType || $[48] !== showAttributes || $[49] !== showProductLabel || $[50] !== showShowroomInfo || $[51] !== showStockInfo || $[52] !== showSwatches || $[53] !== t10 || $[54] !== t11) {
    t12 = {
      ...mergedData,
      showAttributes,
      showProductLabel,
      showShowroomInfo,
      showStockInfo,
      showSwatches,
      productLabelColor: t10,
      campaign,
      isClearance,
      isFavorited,
      position,
      productFamily,
      productType,
      selectedOptionId: t11,
      setSelectedOptionId
    };
    $[41] = campaign;
    $[42] = isClearance;
    $[43] = isFavorited;
    $[44] = mergedData;
    $[45] = position;
    $[46] = productFamily;
    $[47] = productType;
    $[48] = showAttributes;
    $[49] = showProductLabel;
    $[50] = showShowroomInfo;
    $[51] = showStockInfo;
    $[52] = showSwatches;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  const dataWithActions = t12;
  const productCardData = dataWithActions;
  let t13;
  if ($[56] !== analyticsComponentName || $[57] !== disableImageLazyLoading || $[58] !== disablePDPPrefetch || $[59] !== enableMobileCarousel || $[60] !== hidden || $[61] !== isHotspotCard || $[62] !== layout || $[63] !== onClick || $[64] !== onProductClicked || $[65] !== prioritizeImage) {
    t13 = <ProductCardStaticUI analyticsComponentName={analyticsComponentName} disableImageLazyLoading={disableImageLazyLoading} disablePDPPrefetch={disablePDPPrefetch} prioritizeImage={prioritizeImage} hidden={hidden} enableMobileCarousel={enableMobileCarousel} onClick={onClick} onProductClicked={onProductClicked} isHotspotCard={isHotspotCard} layout={layout} />;
    $[56] = analyticsComponentName;
    $[57] = disableImageLazyLoading;
    $[58] = disablePDPPrefetch;
    $[59] = enableMobileCarousel;
    $[60] = hidden;
    $[61] = isHotspotCard;
    $[62] = layout;
    $[63] = onClick;
    $[64] = onProductClicked;
    $[65] = prioritizeImage;
    $[66] = t13;
  } else {
    t13 = $[66];
  }
  let t14;
  if ($[67] !== productCardData || $[68] !== t13) {
    t14 = <ProductCardContext.Provider value={productCardData}>{t13}</ProductCardContext.Provider>;
    $[67] = productCardData;
    $[68] = t13;
    $[69] = t14;
  } else {
    t14 = $[69];
  }
  return t14;
};
ProductCardStatic.propTypes = {
  analyticsComponentName: PropTypes.string,
  campaign: PropTypes.string,
  disablePDPPrefetch: PropTypes.bool,
  enableMobileCarousel: PropTypes.bool,
  filterStateKey: PropTypes.string,
  hidden: PropTypes.bool,
  id: PropTypes.number.isRequired,
  initialOptionId: PropTypes.string,
  isHotspotCard: PropTypes.bool,
  isClearance: PropTypes.bool,
  isFavorited: PropTypes.bool,
  layout: PropTypes.oneOf(['full', 'mini', 'minimal', 'minimal-with-attributes', 'minimal-with-showroom']),
  meta: PropTypes.object,
  moreVariantsCount: PropTypes.number,
  name: PropTypes.string,
  onClick: PropTypes.func,
  // Called alongside Product Clicked, including Ctrl/Cmd-clicks that skip onClick
  onProductClicked: PropTypes.func,
  onDisplayInShowroomSlug: PropTypes.string,
  path: PropTypes.string,
  position: PropTypes.number,
  productFamily: PropTypes.string,
  productLabel: PropTypes.string,
  productType: PropTypes.string,
  quickship: PropTypes.bool,
  secondaryImage: PropTypes.string,
  secondaryImageCropData: PropTypes.object,
  slug: PropTypes.string,
  bypass_stock: PropTypes.bool,
  variantsToShow: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    image: PropTypes.string,
    isOutOfStock: PropTypes.bool,
    label: PropTypes.string,
    minPrice: PropTypes.number,
    originalPrice: PropTypes.number,
    path: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    quickship: PropTypes.bool,
    sku: PropTypes.string
  })),
  variantsTotal: PropTypes.number
};
export default ProductCardStatic;